import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

import AuthLayout from '@/layouts/AuthLayout'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/layouts/AdminLayout'
import ProtectedRoute from '@/components/ProtectedRoute'
import ErrorBoundary from '@/components/ErrorBoundary'
import Spinner from '@/components/ui/Spinner'
import TitleManager from '@/components/TitleManager'
import ScrollToTop from '@/components/ScrollToTop'
import Analytics from '@/components/Analytics'
import CookieConsent from '@/components/CookieConsent'

// ---------------------------------------------------------------------------
// Lazy page imports
// ---------------------------------------------------------------------------

// Public marketing pages
const LandingPage       = React.lazy(() => import('@/pages/LandingPage'))
const ComparePage       = React.lazy(() => import('@/pages/ComparePage'))
const DocsPage          = React.lazy(() => import('@/pages/DocsPage'))
const UseCasesPage      = React.lazy(() => import('@/pages/UseCasesPage'))
const NewsPage          = React.lazy(() => import('@/pages/NewsPage'))
const ArticlePage       = React.lazy(() => import('@/pages/ArticlePage'))
const NotFoundPage      = React.lazy(() => import('@/pages/NotFoundPage'))
const PrivacyPolicyPage = React.lazy(() => import('@/pages/PrivacyPolicyPage'))
const TermsPage         = React.lazy(() => import('@/pages/TermsPage'))
const RefundPolicyPage  = React.lazy(() => import('@/pages/RefundPolicyPage'))

// Auth pages
const LoginPage         = React.lazy(() => import('@/pages/LoginPage'))
const RegisterPage      = React.lazy(() => import('@/pages/RegisterPage'))
const VerifyEmailPage   = React.lazy(() => import('@/pages/VerifyEmailPage'))
const ForgotPasswordPage = React.lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPasswordPage'))
const AskPasswordPage   = React.lazy(() => import('@/pages/AskPasswordPage'))
const OAuthCallbackPage = React.lazy(() => import('@/pages/OAuthCallbackPage'))

// App pages
const DashboardPage  = React.lazy(() => import('@/pages/DashboardPage'))
const LinksPage      = React.lazy(() => import('@/pages/LinksPage'))
const LinkDetailPage = React.lazy(() => import('@/pages/LinkDetailPage'))
const TagsPage       = React.lazy(() => import('@/pages/TagsPage'))
const DomainsPage    = React.lazy(() => import('@/pages/DomainsPage'))
const ApiKeysPage    = React.lazy(() => import('@/pages/ApiKeysPage'))
const BillingPage    = React.lazy(() => import('@/pages/BillingPage'))
const ProfilePage    = React.lazy(() => import('@/pages/ProfilePage'))

// Admin pages
const AdminDashboardPage    = React.lazy(() => import('@/pages/AdminDashboardPage'))
const AdminUsersPage        = React.lazy(() => import('@/pages/AdminUsersPage'))
const AdminUserDetailPage   = React.lazy(() => import('@/pages/AdminUserDetailPage'))
const AdminLinksPage        = React.lazy(() => import('@/pages/AdminLinksPage'))
const AdminPaymentsPage     = React.lazy(() => import('@/pages/AdminPaymentsPage'))
const AdminBillingPlansPage = React.lazy(() => import('@/pages/AdminBillingPlansPage'))
const AdminDomainsPage      = React.lazy(() => import('@/pages/AdminDomainsPage'))

// ---------------------------------------------------------------------------
// Full-screen loading fallback used while lazy chunks are being fetched
// ---------------------------------------------------------------------------

const PageLoader: React.FC = () => (
  <div
    className="flex min-h-screen items-center justify-center bg-background"
    aria-busy="true"
    aria-label="Loading page…"
  >
    <Spinner size="lg" className="text-primary" />
  </div>
)

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

const App: React.FC = () => (
  <ErrorBoundary>
    <TitleManager />
    <Analytics />
    <ScrollToTop />
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* ── Public marketing pages ────────────────────────────────────────── */}
      <Route index element={<LandingPage />} />
      <Route path="compare" element={<ComparePage />} />
      <Route path="docs" element={<DocsPage />} />
      <Route path="use-cases" element={<UseCasesPage />} />
      <Route path="news" element={<NewsPage />} />
      <Route path="news/:slug" element={<ArticlePage />} />
      <Route path="privacy" element={<PrivacyPolicyPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="refunds" element={<RefundPolicyPage />} />

      {/* ── Auth routes (public, centered card layout) ────────────────────── */}
      <Route element={<AuthLayout />}>
        <Route path="login"           element={<LoginPage />} />
        <Route path="register"        element={<RegisterPage />} />
        <Route path="verify-email"    element={<VerifyEmailPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password"  element={<ResetPasswordPage />} />
        <Route path="ask-password"    element={<AskPasswordPage />} />
      </Route>

      {/* ── OAuth callback (no layout — handles redirect programmatically) ── */}
      <Route path="oauth/callback" element={<OAuthCallbackPage />} />

      {/* ── Protected app routes ──────────────────────────────────────────── */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="app">
            {/* Default /app → dashboard */}
            <Route index element={<Navigate to="/app/dashboard" replace />} />

            <Route path="dashboard"  element={<DashboardPage />} />
            <Route path="links"      element={<LinksPage />} />
            <Route path="links/:id"  element={<LinkDetailPage />} />
            <Route path="tags"       element={<TagsPage />} />
            <Route path="domains"    element={<DomainsPage />} />
            <Route path="api-keys"   element={<ApiKeysPage />} />
            <Route path="billing"    element={<BillingPage />} />
            <Route path="profile"    element={<ProfilePage />} />
          </Route>
        </Route>
      </Route>

      {/* ── Protected admin routes ────────────────────────────────────────── */}
      <Route element={<ProtectedRoute requireAdmin />}>
        <Route element={<AdminLayout />}>
          <Route path="admin">
            {/* Default /admin → admin dashboard */}
            <Route index element={<Navigate to="/admin/dashboard" replace />} />

            <Route path="dashboard"     element={<AdminDashboardPage />} />
            <Route path="users"         element={<AdminUsersPage />} />
            <Route path="users/:id"     element={<AdminUserDetailPage />} />
            <Route path="links"         element={<AdminLinksPage />} />
            <Route path="payments"      element={<AdminPaymentsPage />} />
            <Route path="billing-plans" element={<AdminBillingPlansPage />} />
            <Route path="domains"       element={<AdminDomainsPage />} />
          </Route>
        </Route>
      </Route>

      {/* ── 404 catch-all ─────────────────────────────────────────────────── */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    <CookieConsent />
  </ErrorBoundary>
)

export default App
